export const TEMPERATURE_LIMIT_BASES = [
  { value: 'COMPANY_STANDARD', label: 'Standard aziendale' },
  { value: 'HACCP_PLAN', label: 'Piano HACCP approvato' },
  { value: 'MANUFACTURER_PRODUCT', label: 'Indicazione del produttore/prodotto' },
  { value: 'LEGAL_SPECIFIC', label: 'Requisito normativo specifico' },
  { value: 'CUSTOM', label: 'Altro riferimento' },
];

const companyStandardReference = 'Standard aziendale proposto; verificare con piano HACCP e prodotto.';

export const TEMPERATURE_PROFILES = [
  {
    code: 'FROZEN_PRODUCTS',
    label: 'Prodotti congelati',
    equipmentKind: 'FREEZER',
    summary: 'Limite massimo −18°C; intervallo di lavoro consigliato −22°C / −18°C.',
    note: 'Una temperatura più fredda di −22°C resta conforme al limite massimo.',
    preset: {
      limit_mode: 'MAX_ONLY',
      min_temperature_c: '',
      max_temperature_c: '-18',
      operational_target_min_temperature_c: '-22',
      operational_target_max_temperature_c: '-18',
      limit_basis: 'COMPANY_STANDARD',
      basis_reference: companyStandardReference,
    },
  },
  {
    code: 'FISH',
    label: 'Pesce',
    equipmentKind: 'FRIDGE',
    summary: 'Il valore numerico deve provenire dal piano HACCP approvato per il prodotto.',
    note: 'Nessun limite numerico viene precompilato. Inserisci quello previsto dal piano e dalla specifica del prodotto.',
    preset: {
      limit_mode: 'MAX_ONLY',
      min_temperature_c: '',
      max_temperature_c: '',
      operational_target_min_temperature_c: '',
      operational_target_max_temperature_c: '',
      limit_basis: 'HACCP_PLAN',
      basis_reference: '',
    },
    needsPlanValue: true,
  },
  ...[
    ['VEGETABLES', 'Verdure'],
    ['PREPARATIONS', 'Preparazioni'],
    ['POKE_SERVICE', 'Servizio poke'],
    ['SUSHI_DISPLAY', 'Esposizione sushi'],
  ].map(([code, label]) => ({
    code,
    label,
    equipmentKind: 'FRIDGE',
    summary: 'Intervallo aziendale proposto +2°C / +5°C, da verificare con il piano e i prodotti.',
    note: 'È una proposta operativa aziendale, non un limite legale universale.',
    preset: {
      limit_mode: 'RANGE',
      min_temperature_c: '2',
      max_temperature_c: '5',
      operational_target_min_temperature_c: '2',
      operational_target_max_temperature_c: '5',
      limit_basis: 'COMPANY_STANDARD',
      basis_reference: companyStandardReference,
    },
  })),
  {
    code: 'CUSTOM',
    label: 'Configurazione personalizzata',
    equipmentKind: null,
    summary: 'Nessun valore precompilato. Inserisci modalità, limiti e fonte.',
    note: 'Usa solo valori verificati nel piano HACCP o nella documentazione applicabile.',
    preset: {
      limit_mode: 'RANGE',
      min_temperature_c: '',
      max_temperature_c: '',
      operational_target_min_temperature_c: '',
      operational_target_max_temperature_c: '',
      limit_basis: 'CUSTOM',
      basis_reference: '',
    },
    needsManualValues: true,
  },
];

export const TEMPERATURE_PROFILE_BY_CODE = Object.fromEntries(
  TEMPERATURE_PROFILES.map((profile) => [profile.code, profile]),
);

export function profilesForEquipment(equipmentKind) {
  if (!['FRIDGE', 'FREEZER', 'COLD_ROOM'].includes(equipmentKind)) return [];
  return TEMPERATURE_PROFILES.filter(
    (profile) => profile.equipmentKind === null || profile.equipmentKind === equipmentKind,
  );
}

export function temperatureModeLabel(mode) {
  if (mode === 'MAX_ONLY') return 'Controllo del valore massimo';
  if (mode === 'MIN_ONLY') return 'Controllo del valore minimo';
  return 'Intervallo minimo e massimo';
}

export function isTemperatureRuleComplete(rule) {
  const hasMin = rule.min_temperature_c !== '' && rule.min_temperature_c !== null;
  const hasMax = rule.max_temperature_c !== '' && rule.max_temperature_c !== null;
  const limitsValid = rule.limit_mode === 'RANGE'
    ? hasMin && hasMax && Number(rule.min_temperature_c) <= Number(rule.max_temperature_c)
    : rule.limit_mode === 'MAX_ONLY' ? !hasMin && hasMax : hasMin && !hasMax;
  const targetMin = rule.operational_target_min_temperature_c;
  const targetMax = rule.operational_target_max_temperature_c;
  const targetValid = (targetMin === '' || targetMin == null) === (targetMax === '' || targetMax == null)
    && (targetMin === '' || targetMin == null || Number(targetMin) <= Number(targetMax));
  const sourceValid = (!rule.profile_code || Boolean(rule.limit_basis))
    && (rule.limit_basis !== 'LEGAL_SPECIFIC' || Boolean(rule.basis_reference?.trim()))
    && (rule.profile_code !== 'FISH' || rule.limit_basis === 'HACCP_PLAN');
  const profile = TEMPERATURE_PROFILE_BY_CODE[rule.profile_code];
  const profileValid = rule.profile_code !== 'FROZEN_PRODUCTS'
    || (rule.limit_mode === 'MAX_ONLY' && hasMax && targetMin !== '' && targetMin != null
      && targetMax !== '' && targetMax != null);
  const checkTimes = String(rule.check_times_text || '')
    .split(',').map((value) => value.trim()).filter(Boolean);
  const scheduleValid = checkTimes.length >= 1 && checkTimes.length <= 12
    && checkTimes.every((value) => /^([01]\d|2[0-3]):[0-5]\d$/.test(value))
    && new Set(checkTimes).size === checkTimes.length;
  const workflowValid = Number(rule.late_after_minutes) >= 1
    && Number(rule.late_after_minutes) <= 1440
    && ['DISPLAY', 'PROBE'].includes(rule.measurement_method)
    && Boolean(rule.corrective_instructions?.trim())
    && Number(rule.verification_after_minutes) >= 1
    && Number(rule.verification_after_minutes) <= 1440;
  return limitsValid && targetValid && sourceValid && profileValid
    && (!profile || !profile.needsPlanValue || hasMax)
    && scheduleValid && workflowValid;
}
